import { ArrowDropDown } from "@mui/icons-material";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogProps,
  DialogTitle,
  Divider,
  Menu,
  MenuItem,
  Typography
} from "@mui/material";
import { useState } from "react";
import { RequestError } from "../../../services";
import {
  ErrorOnDialog,
} from "../../modals";

export type PropertieType = "string" | "number" | "bigint" | "boolean" | "symbol" | "undefined" | "object" | "function";
export interface MatchPropertiesProps extends DialogProps {
  onFinish?: (matches: { [key: string]: string[]; }) => void;
  title?: string;
  description?: string;
  initialMatches?: { [key: string]: string[]; };
  properties: { [key: string]: PropertieType[]; };
  deseredProperties: { [key: string]: PropertieType; };
}

export const MatchProperties: React.FC<MatchPropertiesProps> = ({
  title,
  onFinish,
  properties,
  description,
  initialMatches,
  deseredProperties,
  ...dialogData
}) => {
  const [matches, setMatches] = useState<{ [key: string]: string[]; }>(initialMatches ?? {});
  const [error, setError] = useState<RequestError>();

  const handleCloseError = () => {
    setError(undefined);
  };

  return <>

    {/* Error */}
    <ErrorOnDialog
      open={Boolean(error)}
      onClose={handleCloseError}
      error={error?.error ?? ""}
      message={error?.message ?? ""}
    />

    {/* Dialog */}
    <Dialog {...dialogData}>
      {/* Title */}
      <DialogTitle>
        {title ?? "Propriedades"}
      </DialogTitle>

      {/* Content */}
      <DialogContent>
        {description &&
          <DialogContentText>{description}</DialogContentText>
        }
        <Box sx={{
          gap: '15px',
          width: "100%",
          height: "100%",
          display: "flex",
          overflowY: 'auto',
          alignItems: "center",
          flexDirection: 'row',
          justifyContent: "center",
        }}>
          {Object.keys(deseredProperties).map((key, i) => {
            return <div key={i}>
              <MatchItem
                propertie={key}
                type={deseredProperties[key]}
                options={properties}
                onSelect={(properties) => {
                  if ("error" in properties) {
                    setError(properties);
                    return;
                  }
                  matches[key] = properties;
                  setMatches({ ...matches });
                }}
              />
              {i !== (Object.keys(deseredProperties).length - 1) &&
                <Divider
                  flexItem
                  orientation="vertical"
                />
              }
            </div>;
          })}

        </Box>

      </DialogContent>

      {/* Actions */}
      <DialogActions>
        <Button onClick={() => dialogData.onClose && dialogData.onClose({}, "escapeKeyDown")}>
          Cancelar
        </Button>
        <Button onClick={() => onFinish && onFinish(matches)}>
          Finalizar
        </Button>
      </DialogActions>
    </Dialog>

  </>;
};

interface MatchItemProps {
  propertie: string;
  type: PropertieType;
  width?: string | number;
  selectedOption?: string[];
  options: { [key: string]: PropertieType[]; };
  onSelect?: (properties: string[] | RequestError) => void;
}
const MatchItem: React.FC<MatchItemProps> = ({
  type,
  width,
  options,
  onSelect,
  propertie,
  selectedOption,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const [selectedProperties, setSelectedProperties] = useState(selectedOption);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    const len = Object.keys(options).filter((value) => typeof options[value].includes(type)).length;
    if (len === 0) {
      if (onSelect) {
        onSelect({
          error: "Match Error",
          message: "Nenhuma correspondência disponível para esta propriedade",
        });
      }
      return;
    }

    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
    if (onSelect) onSelect(selectedProperties ?? []);
  };

  const handleSelect = (propertie: string) => {
    let props = [...selectedProperties ?? []];
    if (props.includes(propertie)) {
      props = props.filter((val) => val !== propertie);
    } else {
      props.push(propertie);
    }
    setSelectedProperties(props);
  };

  return <>
    <Menu
      open={open}
      id="basic-menu"
      anchorEl={anchorEl}
      onClose={() => handleClose()}
      MenuListProps={{ 'aria-labelledby': 'basic-button' }}
    >
      {Object.keys(options).filter((value) => options[value].includes(type)).map((key, i) => {
        return <MenuItem
          key={i}
          onClick={() => handleSelect(key)}
        >
          <Typography fontWeight={selectedProperties?.includes(key) ? "bold" : undefined}>
            {selectedProperties?.includes(key) && selectedProperties.indexOf(key) + 1}
            {selectedProperties?.includes(key) && ". "}
            {key}
          </Typography>
        </MenuItem>;
      })}
    </Menu>

    <Box sx={{
      flex: 1,
      gap: "15px",
      display: "flex",
      alignItems: "center",
      width: width ?? "100%",
      flexDirection: "column",
      justifyContent: "center",
    }}>
      {/* Propriedades */}
      <Typography>{propertie}</Typography>
      <Divider flexItem />
      <Button
        id="basic-button"
        aria-haspopup="true"
        onClick={handleClick}
        endIcon={<ArrowDropDown />}
        aria-expanded={open ? 'true' : undefined}
        aria-controls={open ? 'basic-menu' : undefined}
      >
        Opções
      </Button>
    </Box>
  </>;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function getValue(object: { [key: string]: any; }, properties?: string[], type?: PropertieType, def: any = undefined) {
  for (const key of properties ?? []) {
    if (type && typeof object[key] !== type) continue;
    return object[key];
  }

  return def;
}